// Angular
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
// Rxjs
import { isObservable, Subject, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
// Models
import { CollapseData } from './../datatable-table/interfaces/collapse-data.interface';
import { DataTable, Sort } from './../../interfaces/datatable.interface';
// Libs
import { v4 as uuidv4 } from 'uuid';

// SOLO DEBIESE USARSE SI SE VAN A MOSTRAR COMPONENTES Y NO SOLO HTML
@Component({
  selector: 'app-custom-datatable',
  templateUrl: './custom-datatable.component.html',
  styleUrls: ['./custom-datatable.component.scss']
})
export class CustomDatatableComponent implements OnInit, OnDestroy {
  id: string;
  @Input() datatable: DataTable;

  response;
  data = [];

  dataLoaderSubject$ = new Subject();
  totalSubject$ = new Subject();

  filtros = {};
  ultimoFiltro = null;
  rebuildFiltros = false;
  sorts: Sort[] = [];
  size = 20;
  page = 1;
  total = 0;

  // Loaders
  loading = false;
  calculatingTotal = false;

  constructor() {
    this.id = uuidv4();
  }

  ngOnInit(): void {
    this.configurarHooks();
    this.configurarPaginacion();
    this.configurarSorts();
    this.setLoading(true);
    this.datatable.hooks.onNewDatatableId(this.id);
    this.configurarDataLoaderSubject();
    this.configurarTotalSubject();
    if (!this.datatable.filtrosMagicos) {
      this.cargarDatos();
    }
  }

  configurarHooks() {
    this.datatable.hooks = this.datatable.hooks || {};
    if (!this.datatable.hooks.onNewDatatableId) {
      this.datatable.hooks.onNewDatatableId = (datatableId: string) => { };
    }
    if (!this.datatable.hooks.onLoading) {
      this.datatable.hooks.onLoading = (loading: boolean) => { };
    }
    if (!this.datatable.hooks.onData) {
      this.datatable.hooks.onData = (data: any[]) => { };
    }
    if (!this.datatable.hooks.onService) {
      this.datatable.hooks.onService = (filtros, sorts: Sort[], page: number, limit: number) => { };
    }
    if (!this.datatable.hooks.onResponse) {
      this.datatable.hooks.onResponse = (data: any) => { };
    }
    if (!this.datatable.hooks.onCollapseData) {
      this.datatable.hooks.onCollapseData = (collapseData: CollapseData[]) => { };
    }
  }

  configurarPaginacion() {
    if (!this.datatable.paginacion.limites) {
      this.datatable.paginacion.limites = [10, 20, 30, 50, 100];
    }
    if (!this.datatable.paginacion.limiteIndex && this.datatable.paginacion.limiteIndex !== 0) {
      this.datatable.paginacion.limiteIndex = 1;
    }
    if (!this.datatable.paginacion.mostrarLimites && this.datatable.paginacion.mostrarLimites !== false) {
      this.datatable.paginacion.mostrarLimites = true;
    }
    if (!this.datatable.paginacion.mostrarPaginacion && this.datatable.paginacion.mostrarPaginacion !== false) {
      this.datatable.paginacion.mostrarPaginacion = true;
    }
    if (this.datatable.paginacion.usarTotal == null) {
      this.datatable.paginacion.usarTotal = true;
    }
    this.size = this.datatable.paginacion.limites[this.datatable.paginacion.limiteIndex];
  }

  configurarSorts() {
    if (this.datatable.sortDefectos) {
      this.datatable.sortDefectos.forEach(sort => {
        const columnas = this.datatable.columnas.filter(c => c.sortKey === sort.key);
        sort.columna = columnas.length > 0 ? columnas[0] : null;
        this.sorts.push(sort);
      });
    }
  }

  cargarDatos(force: boolean = false) {
    if (!this.datatable.servicio) {
      this.data = this.datatable.data(null);
      this.total = this.data.length;
      this.datatable.hooks.onData(this.data);
      this.setLoading(false);
      return;
    }
    let filtrosJSONStringified = JSON.stringify(this.filtros);
    if (force || this.ultimoFiltro === null || this.ultimoFiltro !== filtrosJSONStringified) {
      this.dataLoaderSubject$.next(filtrosJSONStringified);
    }
  }

  configurarDataLoaderSubject() {
    this.dataLoaderSubject$
      .pipe(
        switchMap((filtrosJSONStringified: string, index: number) => {
          this.setLoading(true);
          this.datatable.hooks.onService(this.filtros, this.sorts, this.page, this.size);
          const obs = this.datatable.servicio(this.filtros, this.sorts, this.page, this.size);
          this.ultimoFiltro = filtrosJSONStringified;
          return obs;
        })
      )
      .subscribe(response => {
        this.response = response;
        this.data = this.datatable.data(response);
        this.datatable.hooks.onResponse(this.response);
        this.datatable.hooks.onData(this.data);
        if (this.datatable.paginacion && this.datatable.paginacion.usarTotal !== false) {
          this.calculateTotal(response);
        }
        this.setLoading(false);
      })
      .add(() => this.setLoading(false));
  }

  configurarTotalSubject() {
    this.totalSubject$
      .pipe(
        switchMap((filtrosJSONStringified: string, index: number) => {
          const obs = this.datatable.paginacion.total(null) as Observable<number>;
          return obs;
        })
      )
      .subscribe((total: number) => {
        this.total = total;
        this.calculatingTotal = false;
      })
      .add(() => (this.calculatingTotal = false));
  }

  calculateTotal(response) {
    this.calculatingTotal = true;
    if (!response) {
      this.total = this.data.length;
      this.calculatingTotal = false;
      return;
    }
    const totalResponse = this.datatable.paginacion.total(response);

    if (isObservable(totalResponse)) {
      this.totalSubject$.next(this.ultimoFiltro);
    } else {
      this.total = totalResponse || 0;
      this.calculatingTotal = false;
    }
  }

  setLoading(loading: boolean): void {
    this.loading = loading;
    this.datatable.hooks.onLoading(this.loading);
  }

  existeAlMenosUnaColumnaAMostrar(): boolean {
    return this.datatable.columnas.some(column => column.mostrar);
  }

  filtrosCambiados(filtros) {
    this.filtros = filtros;
    this.rebuildFiltros = !this.rebuildFiltros;
    this.page = 1;
    this.cargarDatos();
  }

  sortCambiados(sorts) {
    this.sorts = sorts;
    this.cargarDatos(true);
  }

  paginaCambiada(page) {
    this.page = page;
    this.cargarDatos(true);
  }

  sizeCambiado(size) {
    this.size = size;
    this.cargarDatos(true);
  }

  reloadClicked() {
    this.cargarDatos(true);
  }

  ngOnDestroy(): void {
    if (this.dataLoaderSubject$) {
      this.dataLoaderSubject$.unsubscribe();
    }
    if (this.totalSubject$) {
      this.totalSubject$.unsubscribe();
    }
  }
}
